import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import { NavLink, useRouteMatch } from 'react-router-dom'
import ButtonBase from '@material-ui/core/ButtonBase'
import Collapse from '@material-ui/core/Collapse'
import IconButton from '@material-ui/core/IconButton'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import AppsIcon from 'components/material-symbol/icons/Apps'
import CloseIcon from 'components/material-symbol/icons/Close'
import ExpandMoreIcon from 'components/material-symbol/icons/ExpandMore'
import OpenInNewIcon from 'components/material-symbol/icons/OpenInNew'
import brandOptions from 'components/shell/brand-options'
import { NAV_ICON_CENTER, NAV_LABEL_LEFT } from './styles'

const LIST_ID = 'drawer-app-switcher-list'

// App rows share the nav items' icon centre line and label edge.
const ITEM_INSET = 12
const APP_ICON_SIZE = 24
const APP_ICON_LEFT = NAV_ICON_CENTER - APP_ICON_SIZE / 2

const useStyles = makeStyles(theme => ({
  root: {
    borderBottom: '1px solid #E0E0E0',
    paddingBottom: theme.spacing(1),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    minHeight: 61,
    padding: theme.spacing(1, 0.5, 0, 1.5),
  },
  currentApp: {
    flex: 1,
    minWidth: 0,
    justifyContent: 'flex-start',
    gap: theme.spacing(1.25),
    padding: theme.spacing(1, 1.25),
    border: '1px solid #E0E0E0',
    borderRadius: 12,
    textAlign: 'left',
    '&:hover': {
      backgroundColor: theme.palette.sidebar.hoverOverlay,
    },
    '&.Mui-focusVisible': {
      backgroundColor: theme.palette.sidebar.focusOverlay,
    },
  },
  appIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    flexShrink: 0,
    color: theme.palette.primary.main,
  },
  currentAppText: {
    flex: 1,
    minWidth: 0,
  },
  currentAppLabel: {
    fontSize: 16,
    lineHeight: '20px',
    fontWeight: 600,
    color: theme.palette.sidebar.itemText,
  },
  currentAppHint: {
    fontSize: 12,
    lineHeight: '16px',
  },
  chevron: {
    color: theme.palette.text.secondary,
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  chevronOpen: {
    transform: 'rotate(180deg)',
  },
  closeButton: {
    color: theme.palette.text.secondary,
  },
  list: {
    padding: theme.spacing(0.5, 0, 0),
  },
  sectionLabel: {
    display: 'block',
    padding: theme.spacing(1, 3, 0.5),
    paddingLeft: APP_ICON_LEFT,
    fontSize: 12,
    lineHeight: '16px',
    fontWeight: 600,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  appItem: {
    color: theme.palette.sidebar.itemText,
    borderRadius: theme.palette.sidebar.itemBorderRadius,
    marginLeft: ITEM_INSET,
    marginRight: ITEM_INSET,
    marginBottom: 4,
    width: `calc(100% - ${ITEM_INSET * 2}px)`,
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: APP_ICON_LEFT - ITEM_INSET,
    minHeight: 44,
    '&:hover': {
      backgroundColor: theme.palette.sidebar.hoverOverlay,
    },
  },
  appItemIcon: {
    minWidth: NAV_LABEL_LEFT - APP_ICON_LEFT,
  },
  appItemIconBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: APP_ICON_SIZE,
    height: APP_ICON_SIZE,
    color: theme.palette.primary.main,
    '& svg': {
      width: APP_ICON_SIZE,
      height: APP_ICON_SIZE,
      fontSize: APP_ICON_SIZE,
    },
  },
  appItemLabel: {
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 500,
  },
  externalIcon: {
    fontSize: 16,
    color: theme.palette.text.secondary,
  },
}))

export default function DrawerAppSwitcher({
  isDrawerOpen = true,
  onNavigate,
  onClose,
}) {
  const classes = useStyles()
  const { path: routeMatch } = useRouteMatch()
  const [isOpen, setIsOpen] = useState(false)

  // The drawer stays mounted while closed, so collapse the list ourselves.
  useEffect(() => {
    if (!isDrawerOpen) setIsOpen(false)
  }, [isDrawerOpen])

  const currentApp = brandOptions.find(brand => brand.path === routeMatch)
  const otherApps = brandOptions.filter(brand => brand !== currentApp)

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <ButtonBase
          className={classes.currentApp}
          onClick={() => setIsOpen(open => !open)}
          aria-expanded={isOpen}
          aria-controls={LIST_ID}
          data-testid="drawer-app-switcher"
        >
          <span className={classes.appIcon}>
            {currentApp ? currentApp.logo : <AppsIcon color="secondary" />}
          </span>
          <span className={classes.currentAppText}>
            <Typography className={classes.currentAppLabel} noWrap>
              {currentApp ? currentApp.label : 'PGA apps'}
            </Typography>
            <Typography
              className={classes.currentAppHint}
              color="textSecondary"
              noWrap
            >
              Switch app
            </Typography>
          </span>
          <ExpandMoreIcon
            className={clsx(classes.chevron, {
              [classes.chevronOpen]: isOpen,
            })}
          />
        </ButtonBase>
        <IconButton
          aria-label="close menu"
          onClick={onClose}
          className={classes.closeButton}
        >
          <CloseIcon />
        </IconButton>
      </div>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <List id={LIST_ID} className={classes.list}>
          <Typography
            component="li"
            color="textSecondary"
            className={classes.sectionLabel}
          >
            Switch to
          </Typography>
          {otherApps.map(app => {
            const linkProps = app.external
              ? {
                component: 'a',
                href: app.path,
                target: '_blank',
                rel: 'noopener noreferrer',
              }
              : { component: NavLink, to: app.path }
            return (
              <ListItem
                key={app.label}
                button
                className={classes.appItem}
                onClick={app.external ? undefined : onNavigate}
                {...linkProps}
              >
                <ListItemIcon className={classes.appItemIcon}>
                  <span className={classes.appItemIconBox}>{app.logo}</span>
                </ListItemIcon>
                <ListItemText
                  primary={app.label}
                  primaryTypographyProps={{ className: classes.appItemLabel }}
                />
                {app.external && (
                  <OpenInNewIcon
                    className={classes.externalIcon}
                    aria-label="opens in a new tab"
                  />
                )}
              </ListItem>
            )
          })}
        </List>
      </Collapse>
    </div>
  )
}
